import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaSearch,
  FaSyncAlt,
  FaUsers,
  FaSpinner,
  FaCalendarCheck,
  FaClock,
  FaFilter,
  FaCheckCircle,
  FaSignOutAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import SummaryApi from "../common";
import { getEmployeeImageUrl } from "../utils/helpers";
import nodata from "../assest/nodata.png";

const THEME = {
  primary: "#172C57",
  primaryHover: "#1E3A6E",
  primaryLight: "rgba(23, 44, 87, 0.08)",
};

const AllActiveEmployees = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const isTodayParam = searchParams.get("filter") === "today";
  const [viewMode, setViewMode] = useState(isTodayParam ? "today" : "all");

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Filter & sort states
  const [loginStatusFilter, setLoginStatusFilter] = useState("all"); // 'all' | 'active' | 'logged_out'
  const [sortOrder, setSortOrder] = useState("desc"); // 'desc' | 'asc'

  // Helper: check if a valid logout timestamp exists (ignores 0000-00-00, null, empty)
  const hasValidLogoutTime = (dateStr) => {
    if (!dateStr || dateStr === "null") return false;
    const str = String(dateStr).trim();
    if (!str || str.startsWith("0000-00-00") || str.startsWith("0000/00/00")) return false;
    const dt = new Date(str.replace(/\.\d+$/, "").replace(" ", "T"));
    return !isNaN(dt.getTime());
  };

  // Helper: session is actively logged in if there is no valid logout timestamp
  const isSessionActive = (session) => {
    if (!session) return false;
    const hasLoggedOut = hasValidLogoutTime(session.logout_date);
    return !hasLoggedOut && String(session.is_active) !== "0";
  };

  // Helper: format timestamp without microseconds
  const formatDateTime = (dateStr) => {
    if (!dateStr || dateStr === "null") return "—";
    const str = String(dateStr).trim();
    if (!str || str.startsWith("0000-00-00")) return "—";
    const cleaned = str.replace(/\.\d+$/, "");
    try {
      const dt = new Date(cleaned.replace(" ", "T"));
      if (isNaN(dt.getTime())) return cleaned;
      return dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch {
      return cleaned;
    }
  };

  // Fetch Attendance Records
  const fetchEmployees = useCallback(async (mode = viewMode) => {
    setLoading(true);
    try {
      const targetApi =
        mode === "today"
          ? SummaryApi.TodayActiveEmployee
          : SummaryApi.AllActiveEmployee;

      const endpoint = targetApi?.url;

      if (!endpoint) {
        toast.error("Endpoint URL is not configured in SummaryApi.");
        setLoading(false);
        return;
      }

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: "Bearer sandeep_university_916",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      let list = [];
      if (Array.isArray(result)) {
        list = result;
      } else if (Array.isArray(result?.data)) {
        list = result.data;
      }

      setEmployees(list);

      if (list.length === 0 && result?.message) {
        toast.info(result.message);
      }
    } catch (error) {
      console.error("Error fetching employee attendance:", error);
      toast.error("Failed to load attendance records.");
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [viewMode]);

  useEffect(() => {
    fetchEmployees(viewMode);
  }, [viewMode, fetchEmployees]);

  const handleModeChange = (newMode) => {
    setViewMode(newMode);
    setPageIndex(0);
    if (newMode === "today") {
      setSearchParams({ filter: "today" });
    } else {
      setSearchParams({});
    }
  };

  // Aggregate stats
  const counts = useMemo(() => {
    let active = 0;
    let loggedOut = 0;

    employees.forEach((emp) => {
      if (isSessionActive(emp)) active += 1;
      else loggedOut += 1;
    });

    return {
      total: employees.length,
      active,
      loggedOut,
    };
  }, [employees]);

  // Filter & Search Pipeline
  const filteredEmployees = useMemo(() => {
    let result = [...employees];

    // Status filter
    if (loginStatusFilter === "active") {
      result = result.filter((emp) => isSessionActive(emp));
    } else if (loginStatusFilter === "logged_out") {
      result = result.filter((emp) => !isSessionActive(emp));
    }

    // Search filter
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      result = result.filter((emp) => {
        const nameMatch = (emp.employee_name || "").toLowerCase().includes(q);
        const idMatch = String(emp.employee_id || "").toLowerCase().includes(q);
        const attendanceMatch = String(emp.emp_attendance_id || "").includes(q);
        const loginMatch = String(emp.login_date || "").includes(q);
        return nameMatch || idMatch || attendanceMatch || loginMatch;
      });
    }

    // Sort by login timestamp
    result.sort((a, b) => {
      const timeA = new Date(String(a.login_date || "").replace(/\.\d+$/, "").replace(" ", "T")).getTime() || 0;
      const timeB = new Date(String(b.login_date || "").replace(/\.\d+$/, "").replace(" ", "T")).getTime() || 0;
      return sortOrder === "desc" ? timeB - timeA : timeA - timeB;
    });

    return result;
  }, [employees, loginStatusFilter, searchTerm, sortOrder]);

  const totalPages = Math.ceil(filteredEmployees.length / pageSize) || 1;
  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredEmployees.slice(start, start + pageSize);
  }, [filteredEmployees, pageIndex, pageSize]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 sm:p-6 space-y-5 text-slate-800">
      {/* Top Header Card */}
      <div className="bg-white rounded-2xl border border-slate-200/90 px-5 py-4 shadow-xs flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors cursor-pointer"
            title="Go Back"
          >
            <FaArrowLeft size={14} />
          </button>
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center text-white shrink-0 shadow-xs"
            style={{ backgroundColor: THEME.primary }}
          >
            <FaUsers size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg font-bold" style={{ color: THEME.primary }}>
                {viewMode === "today" ? "Today's Employee Logins" : "All Active Employees"}
              </h1>
              <span className="bg-blue-50 text-blue-700 border border-blue-200 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                Total: {counts.total}
              </span>
            </div>
            <p className="text-xs text-slate-500">
              {viewMode === "today"
                ? "Live check-in records for today"
                : "Master attendance records from the server"}
            </p>
          </div>
        </div>

        {/* View Switcher (Today vs All) */}
        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs font-semibold self-stretch sm:self-auto justify-center">
          <button
            onClick={() => handleModeChange("today")}
            className={`px-4 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
              viewMode === "today"
                ? "bg-white text-slate-900 shadow-xs"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <FaClock size={12} />
            Today's Logins
          </button>
          <button
            onClick={() => handleModeChange("all")}
            className={`px-4 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
              viewMode === "all"
                ? "bg-white text-slate-900 shadow-xs"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <FaUsers size={12} />
            All Time
          </button>
        </div>
      </div>

      {/* Control & Filter Bar */}
      <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-xs flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3">
        {/* Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
          <button
            onClick={() => {
              setLoginStatusFilter("all");
              setPageIndex(0);
            }}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              loginStatusFilter === "all"
                ? "bg-slate-900 text-white shadow-xs"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            <FaFilter size={10} />
            All ({counts.total})
          </button>

          <button
            onClick={() => {
              setLoginStatusFilter("active");
              setPageIndex(0);
            }}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              loginStatusFilter === "active"
                ? "bg-emerald-600 text-white shadow-xs"
                : "bg-emerald-50 text-emerald-700 border border-emerald-200/80 hover:bg-emerald-100"
            }`}
          >
            <FaCheckCircle size={10} />
            Active Now ({counts.active})
          </button>

          <button
            onClick={() => {
              setLoginStatusFilter("logged_out");
              setPageIndex(0);
            }}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              loginStatusFilter === "logged_out"
                ? "bg-slate-600 text-white shadow-xs"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            <FaSignOutAlt size={10} />
            Logged Out ({counts.loggedOut})
          </button>
        </div>

        {/* Search, Sort & Refresh */}
        <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap">
          {/* Sort Selector */}
          <div className="flex items-center gap-1 text-xs">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-xl px-2.5 py-2 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#172C57]"
            >
              <option value="desc">🕒 Recent Login First</option>
              <option value="asc">🕒 Earliest Login First</option>
            </select>
          </div>

          {/* Search Field */}
          <div className="relative flex-1 sm:w-56">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
            <input
              type="text"
              placeholder="Search employee..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageIndex(0);
              }}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#172C57] transition-all"
            />
          </div>

          {/* Refresh */}
          <button
            onClick={() => fetchEmployees(viewMode)}
            className="p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors cursor-pointer shrink-0"
            title="Refresh List"
          >
            <FaSyncAlt size={13} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Main Table Container */}
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
        {loading ? (
          <div className="py-24 text-center">
            <FaSpinner
              className="animate-spin text-3xl mx-auto mb-3"
              style={{ color: THEME.primary }}
            />
            <p className="text-xs text-slate-500 font-medium">
              Loading {viewMode === "today" ? "today's logins" : "attendance records"}...
            </p>
          </div>
        ) : paginatedData.length === 0 ? (
          <div className="py-20 text-center flex flex-col items-center">
            <img src={nodata} alt="No Data" className="w-32 mb-3 opacity-80" />
            <p className="text-sm font-semibold text-slate-700">No matching records found</p>
            <p className="text-xs text-slate-400 mt-0.5">
              {searchTerm || loginStatusFilter !== "all"
                ? "Try clearing filters or search keywords"
                : "No employee logins recorded for this period"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-[#F8FAFC] border-b border-slate-200 text-slate-600 font-semibold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="py-3.5 px-4 w-12 text-center">S.No</th>
                  <th className="py-3.5 px-4">Employee</th>
                  <th className="py-3.5 px-4">Employee ID</th>
                  <th className="py-3.5 px-4">Attendance ID</th>
                  <th className="py-3.5 px-4">Login Timestamp</th>
                  <th className="py-3.5 px-4">Logout Timestamp</th>
                  <th className="py-3.5 px-4 text-center">Current Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedData.map((emp, index) => {
                  const active = isSessionActive(emp);
                  const validLogout = hasValidLogoutTime(emp.logout_date);

                  return (
                    <tr
                      key={emp.emp_attendance_id || index}
                      className="hover:bg-slate-50/70 transition-colors"
                    >
                      <td className="py-3.5 px-4 text-center font-medium text-slate-500">
                        {pageIndex * pageSize + index + 1}
                      </td>

                      {/* Employee Avatar & Name */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <img
                              src={getEmployeeImageUrl(emp.employee_image)}
                              alt={emp.employee_name}
                              className="w-10 h-10 rounded-full object-cover border border-slate-200 shrink-0"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  emp.employee_name || "Employee"
                                )}&background=172C57&color=fff`;
                              }}
                            />
                            {active && (
                              <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white animate-pulse" />
                            )}
                          </div>
                          <div>
                            <p className="font-bold text-slate-900 leading-tight">
                              {emp.employee_name || "N/A"}
                            </p>
                            <p className="text-[11px] text-slate-400 mt-0.5">
                              Emp #{emp.employee_id}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Employee ID */}
                      <td className="py-3.5 px-4 font-semibold text-slate-700">
                        <span className="bg-slate-100 px-2 py-1 rounded-md text-[11px]">
                          {emp.employee_id}
                        </span>
                      </td>

                      {/* Attendance ID */}
                      <td className="py-3.5 px-4 text-slate-600 font-medium">
                        #{emp.emp_attendance_id}
                      </td>

                      {/* Login Timestamp */}
                      <td className="py-3.5 px-4 text-slate-800 font-medium">
                        <div className="flex items-center gap-1.5">
                          <FaCalendarCheck size={11} className="text-emerald-600" />
                          <span>{formatDateTime(emp.login_date)}</span>
                        </div>
                      </td>

                      {/* Logout Timestamp */}
                      <td className="py-3.5 px-4 text-slate-500">
                        <div className="flex items-center gap-1.5">
                          <FaClock size={11} className="text-slate-400" />
                          <span>
                            {validLogout
                              ? formatDateTime(emp.logout_date)
                              : "Still Logged In"}
                          </span>
                        </div>
                      </td>

                      {/* Current Status */}
                      <td className="py-3.5 px-4 text-center">
                        {active ? (
                          <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 rounded-full font-bold text-[10px]">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 border border-slate-200 px-2.5 py-0.5 rounded-full font-medium text-[10px]">
                            Logged Out
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {!loading && filteredEmployees.length > 0 && (
          <div className="px-5 py-3.5 border-t border-slate-200/80 bg-[#F8FAFC] flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <span>Rows per page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPageIndex(0);
                }}
                className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs focus:outline-none"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="text-slate-400 ml-2">
                Showing {pageIndex * pageSize + 1}–
                {Math.min((pageIndex + 1) * pageSize, filteredEmployees.length)} of{" "}
                {filteredEmployees.length} records
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
                disabled={pageIndex === 0}
                className="px-3 py-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition-colors cursor-pointer"
              >
                Previous
              </button>
              <span className="px-2 font-semibold text-slate-700">
                Page {pageIndex + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPageIndex((prev) => Math.min(prev + 1, totalPages - 1))}
                disabled={pageIndex >= totalPages - 1}
                className="px-3 py-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition-colors cursor-pointer"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllActiveEmployees;